import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import { welcomeLetter, benefitsFlyer, howToSignUp, faq, quickStartCard } from '../../data/marketingTemplates';
import '../../styles/global.css';

const TEMPLATES = [
  { key: 'welcome', label: 'Welcome Letter', icon: '✉️', desc: 'Warm intro letter for new tenant welcome packages', fn: welcomeLetter },
  { key: 'benefits', label: 'Benefits Flyer', icon: '📄', desc: 'One-page visual flyer with pricing and features', fn: benefitsFlyer },
  { key: 'signup', label: 'How to Sign Up', icon: '📋', desc: 'Step-by-step sign-up instructions with QR code', fn: howToSignUp },
  { key: 'faq', label: 'FAQ Sheet', icon: '❓', desc: '12 common questions answered clearly', fn: faq },
  { key: 'card', label: 'Quick Start Card', icon: '🃏', desc: 'Half-page card, 2 per sheet, perfect for packets', fn: quickStartCard },
];

const now = new Date();
const DEFAULT_DATE = now.toLocaleString('default', { month: 'long', year: 'numeric' });

export default function MarketingMaterials() {
  const [qrSrc, setQrSrc] = useState('');
  const [activeKey, setActiveKey] = useState(null);
  const [fields, setFields] = useState({
    price: 'XX', building: 'your building', email: 'info@affordable-telecom.com', date: DEFAULT_DATE
  });
  const [previewHtml, setPreviewHtml] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    QRCode.toDataURL('https://portal.affordable-telecom.com/get-started', {
      width: 200, margin: 1, color: { dark: '#1e3a5f', light: '#ffffff' }
    }).then(url => setQrSrc(url)).catch(() => {});
  }, []);

  const getHtml = useCallback((key) => {
    const tmpl = TEMPLATES.find(t => t.key === key);
    if (!tmpl) return '';
    return tmpl.fn({ ...fields, qrSrc });
  }, [fields, qrSrc]);

  useEffect(() => {
    if (!activeKey) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewHtml(getHtml(activeKey));
    }, 500);
  }, [fields, activeKey, getHtml]);

  const openCard = (key) => {
    if (activeKey === key) { setActiveKey(null); return; }
    setActiveKey(key);
    setPreviewHtml(getHtml(key));
  };

  const openForPdf = () => {
    const html = getHtml(activeKey);
    const blob = new Blob([html], { type: 'text/html' });
    window.open(URL.createObjectURL(blob), '_blank');
  };

  const downloadHtml = () => {
    const tmpl = TEMPLATES.find(t => t.key === activeKey);
    const html = getHtml(activeKey);
    const blob = new Blob([html], { type: 'text/html' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `affordable_telecom_${activeKey}.html`;
    a.click();
    toast.success(`${tmpl.label} downloaded`);
  };

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main className="main-content">
          <div className="page-header">
            <div className="page-title">Marketing Materials</div>
            <div className="page-sub">Edit and download printable marketing documents for tenant onboarding packages.</div>
          </div>

          {/* Template Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 24 }}>
            {TEMPLATES.map(t => (
              <div key={t.key} className="card" style={{ cursor: 'pointer', border: activeKey === t.key ? '2px solid var(--teal)' : '1px solid var(--border)' }}
                onClick={() => openCard(t.key)}>
                <div className="card-body">
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{t.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 4 }}>{t.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-sub)', lineHeight: 1.5, marginBottom: 12 }}>{t.desc}</div>
                  <button className={`btn btn-sm ${activeKey === t.key ? 'btn-primary' : 'btn-outline'}`} style={{ width: '100%' }}>
                    {activeKey === t.key ? 'Editing ✓' : 'Edit & Download'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Editor Panel */}
          {activeKey && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-header">
                <div className="card-title">
                  {TEMPLATES.find(t => t.key === activeKey)?.icon} {TEMPLATES.find(t => t.key === activeKey)?.label} — Editor
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={downloadHtml}>⬇ Download HTML</button>
                  <button className="btn btn-primary btn-sm" onClick={openForPdf}>🖨 Open for PDF</button>
                </div>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Monthly Price ($)</label>
                    <input className="form-input" value={fields.price}
                      onChange={e => setFields(f => ({ ...f, price: e.target.value }))}
                      placeholder="e.g. 49.99" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Building Name</label>
                    <input className="form-input" value={fields.building}
                      onChange={e => setFields(f => ({ ...f, building: e.target.value }))}
                      placeholder="your building" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Contact Email</label>
                    <input className="form-input" value={fields.email}
                      onChange={e => setFields(f => ({ ...f, email: e.target.value }))}
                      placeholder="info@affordable-telecom.com" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Date</label>
                    <input className="form-input" value={fields.date}
                      onChange={e => setFields(f => ({ ...f, date: e.target.value }))}
                      placeholder="April 2026" />
                  </div>
                </div>

                <div style={{ background: 'var(--light-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 12, color: 'var(--text-sub)' }}>
                  💡 The preview updates automatically as you type. Click <strong>Open for PDF</strong> to print — in Chrome use <strong>Cmd+P → Save as PDF</strong>.
                </div>

                {/* Live Preview */}
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'white' }}>
                  <div style={{ background: 'var(--light-bg)', padding: '8px 14px', fontSize: 11, color: 'var(--text-sub)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Live Preview</span>
                    <span style={{ fontSize: 10 }}>Scroll within preview to see full document</span>
                  </div>
                  <iframe
                    srcDoc={previewHtml}
                    style={{ width: '100%', height: 600, border: 'none', transform: 'scale(0.85)', transformOrigin: 'top left', width: '117%' }}
                    title="preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            </div>
          )}

          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
